import {
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type SVGProps,
  type TextareaHTMLAttributes,
  type ThHTMLAttributes,
  type TdHTMLAttributes,
} from "react";
import { Link } from "react-router-dom";
import { PASSWORD_REQUIREMENTS } from "@autorepair/contracts";
import { applyDecimalTransform, applySameLengthTransform } from "../lib/inputCursor";

/**
 * The app's own primitive set — see docs/FRONTEND_BACKLOG.md Round 59.
 * Deliberately NOT routed through `index.css`'s `@theme` tokens
 * (`--color-brand` etc.) — those were the classic theme's own design
 * language (that theme is gone — see docs/TRAPS.md and
 * docs/NEW_CHAT_PROMPT.md); this file styles directly off the mockup's
 * literal hex/px values (see
 * tmp/design_handoff_bay2bill_redesign/README.md's "Design Tokens").
 */

/** Generic loading indicator, used everywhere a query/mutation is in
 * flight. Moved here from the now-deleted classic `ui.tsx` — this app has
 * only ever had one visual language since Classic was removed. */
export function Spinner({ label = "Loading…" }: { label?: string }) {
  return (
    <div className="flex items-center gap-3 py-8 text-[14px] text-[#4a5361]">
      <span className="h-6 w-6 animate-spin rounded-full border-4 border-[#e2e7ee] border-t-[#1d5fd6]" aria-hidden />
      {label}
    </div>
  );
}

/** Shown on the login screen and every authenticated page outside
 * production, so nobody mistakes a dev/staging environment for the real
 * thing. Moved here from the now-deleted classic `ui.tsx`. */
export function DevEnvironmentBanner() {
  if (import.meta.env.PROD) return null;
  return (
    <div className="border-b-2 border-[#9a6700] bg-[#fff4e0] px-4 py-2 text-center text-[13.5px] font-semibold text-[#9a6700]">
      Development environment — do not enter real customer data.
    </div>
  );
}

/** Roboto Mono, uppercase, wide letter-spacing — the mockup's convention
 * for eyebrows/metadata/table-headers. `weight` picks 500 vs 600 (both are
 * the only two weights this app ever loads — see index.css's @font-face). */
export function Eyebrow({
  children,
  weight = 600,
  className = "",
}: {
  children: ReactNode;
  weight?: 500 | 600 | 700;
  className?: string;
}) {
  const fontWeightClass = weight === 700 ? "font-bold" : weight === 600 ? "font-semibold" : "font-medium";
  return (
    <div
      className={`font-mono uppercase tracking-[.14em] ${fontWeightClass} ${className}`}
      style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace" }}
    >
      {children}
    </div>
  );
}

// Round-2 button redesign (2026-09-29, see docs/FRONTEND_BACKLOG.md) — pill-shaped, with
// depth/motion/focus feedback prototyped side-by-side against the shipped look in
// tmp/button.html before landing here. `ease-in-out` is Tailwind's cubic-bezier(.4,0,.2,1),
// matching the prototype's easing exactly.
const MODERN_BUTTON_BASE =
  "relative inline-flex items-center justify-center gap-2 rounded-full text-[14px] font-semibold tracking-[.01em] transition-[color,background-color,border-color,box-shadow,transform] duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed min-h-11 px-5 active:scale-[0.97]";

const MODERN_BUTTON_VARIANTS = {
  // Now matches the marketing site's "Request access" button color-for-color
  // (bay2bill-web/index.html's --blue #176fa8 → --teal-dark #178f91) instead of the app's old
  // navy/teal, so the primary CTA reads the same on the app and the website. The embossed
  // border/inset lines are the "dark glass pill" depth cue the owner asked to match from a
  // reference photo.
  primary:
    "text-white font-[750] bg-[linear-gradient(135deg,#176fa8,#178f91)] border-[1.5px] border-[rgba(255,255,255,.45)] shadow-[inset_0_1px_0_rgba(255,255,255,.45),inset_0_-1.5px_0_rgba(0,0,0,.35),0_9px_23px_rgba(23,111,168,.3)] hover:brightness-110 hover:border-[rgba(255,255,255,.6)] hover:shadow-[inset_0_1px_0_rgba(255,255,255,.55),inset_0_-1.5px_0_rgba(0,0,0,.35),0_12px_28px_rgba(23,111,168,.38)] hover:-translate-y-[2px] active:translate-y-0 active:brightness-[1.02] active:shadow-[inset_0_1px_0_rgba(255,255,255,.3),inset_0_-1px_0_rgba(0,0,0,.3),0_4px_10px_rgba(23,111,168,.25)] focus-visible:outline-none focus-visible:shadow-[inset_0_1px_0_rgba(255,255,255,.45),inset_0_-1.5px_0_rgba(0,0,0,.35),0_0_0_2px_#fff,0_0_0_4.5px_rgba(23,111,168,.55)]",
  secondary:
    "bg-white text-[#071a33] border border-[#e2e7ee] hover:border-[#0f6d86] hover:text-[#0f6d86] hover:shadow-[0_3px_8px_rgba(7,26,51,.08)] hover:-translate-y-[1px] active:translate-y-0 active:bg-[#f6f8fb] active:shadow-none focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4.5px_rgba(15,109,134,.55)]",
  ghost:
    "bg-transparent text-[#0f6d86] hover:bg-[#e6f1f5] active:bg-[#d6e8ee] focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4.5px_rgba(15,109,134,.55)]",
  // Destructive actions (delete customer/vehicle, etc.) — the mockup has no
  // dedicated danger-button swatch of its own (see ModernStatusPill's own
  // comment on this same gap for "void"), so this reuses ui.tsx's existing
  // `--color-danger` (#b3261e), the app's one established red, rather than
  // inventing a second one. Same embossed depth treatment as primary.
  danger:
    "text-white font-[750] bg-[#b3261e] border-[1.5px] border-[rgba(255,255,255,.4)] shadow-[inset_0_1px_0_rgba(255,255,255,.4),inset_0_-1.5px_0_rgba(0,0,0,.35),0_9px_23px_rgba(179,38,30,.28)] hover:brightness-110 hover:border-[rgba(255,255,255,.55)] hover:shadow-[inset_0_1px_0_rgba(255,255,255,.5),inset_0_-1.5px_0_rgba(0,0,0,.35),0_12px_28px_rgba(179,38,30,.36)] hover:-translate-y-[2px] active:translate-y-0 active:shadow-[inset_0_1px_0_rgba(255,255,255,.25),inset_0_-1px_0_rgba(0,0,0,.3),0_4px_10px_rgba(179,38,30,.22)] focus-visible:outline-none focus-visible:shadow-[inset_0_1px_0_rgba(255,255,255,.4),inset_0_-1.5px_0_rgba(0,0,0,.35),0_0_0_2px_#fff,0_0_0_4.5px_rgba(179,38,30,.4)]",
};

/** Same classes `ModernButton` applies to a real `<button>`, for the rare non-`<button>`
 * (e.g. an `<a>` that needs button-primary styling) — a single source of truth so the two
 * never drift apart the way `AppShellModern.tsx`'s header "New Work Order" link once did. */
export function modernButtonClassName(variant: keyof typeof MODERN_BUTTON_VARIANTS = "primary", className = "") {
  return `${MODERN_BUTTON_BASE} ${MODERN_BUTTON_VARIANTS[variant]} ${className}`;
}

export function ModernButton({
  variant = "primary",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: keyof typeof MODERN_BUTTON_VARIANTS }) {
  return <button className={modernButtonClassName(variant, className)} {...props} />;
}

/** Small stroke icons for buttons (print / download / email) — sit before the label; they inherit
 * the button's text colour. */
function buttonIconProps(props: SVGProps<SVGSVGElement>) {
  return {
    width: 16,
    height: 16,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
    className: "flex-none",
    ...props,
  };
}
export function PrintIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...buttonIconProps(props)}>
      <path d="M7 9V3h10v6" />
      <rect x="4" y="9" width="16" height="8" rx="2" />
      <path d="M7 14h10v7H7z" />
    </svg>
  );
}
export function DownloadIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...buttonIconProps(props)}>
      <path d="M12 4v11" />
      <path d="m7 11 5 5 5-5" />
      <path d="M5 20h14" />
    </svg>
  );
}
export function InvoiceIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...buttonIconProps(props)}>
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M14 3v5h5" />
      <path d="M9 13h7M9 17h7" />
    </svg>
  );
}
export function EmailIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg {...buttonIconProps(props)}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

/** Pale-teal "lookup / navigate" button (e.g. "+ Predefined Items", "Edit Customer") — a softer
 * sibling of ModernButton for secondary actions. Sized to match ModernButton's own 44px pill
 * footprint (round-2 button redesign) instead of its old undersized 32px/8px-radius look —
 * `TINT_TEXT` (12.5px) is deliberately NOT reused here since it's still ModernInfoTip's size. */
// Colours of the pale-teal look, shared by ModernTintButton and ModernInfoTip so they stay in sync.
const TINT_SURFACE = "border border-[#c9dfe6] bg-[#e6f1f5]";
const TINT_TEXT = "text-[12.5px] font-semibold text-[#0f6d86]";

export function ModernTintButton({ className = "", ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-full ${TINT_SURFACE} px-5 text-[14px] font-semibold text-[#0f6d86] transition-[background-color,box-shadow,transform] duration-200 ease-in-out hover:bg-[#d6e8ee] hover:shadow-[0_3px_8px_rgba(15,109,134,.12)] hover:-translate-y-[1px] active:translate-y-0 active:scale-[0.97] active:shadow-none focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_#fff,0_0_0_4.5px_rgba(15,109,134,.55)] disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    />
  );
}

/** Borderless-card look the mockup uses everywhere: white, 1px `#e2e7ee`
 * border, 14px radius, no shadow. `dark` is the one recurring exception —
 * the 4th stat card and similar "owed to the shop"-style emphasis blocks. */
export function ModernCard({
  children,
  className = "",
  dark = false,
}: {
  children: ReactNode;
  className?: string;
  dark?: boolean;
}) {
  return (
    <div
      className={`rounded-[14px] border p-[18px] ${
        dark ? "border-[#071a33] bg-[#071a33]" : "border-[#e2e7ee] bg-white"
      } ${className}`}
    >
      {children}
    </div>
  );
}

/** One of the Dashboard's 4 stat cards — eyebrow label, big tabular-nums
 * number, small muted subtext. `dark` matches ModernCard's dark variant
 * (used for exactly one card — "Owed to the shop" — per the mockup). */
export function StatCard({
  label,
  value,
  subtext,
  dark = false,
  valueClassName = "",
  className = "",
}: {
  label: string;
  value: ReactNode;
  subtext?: ReactNode;
  dark?: boolean;
  valueClassName?: string;
  className?: string;
}) {
  return (
    <ModernCard dark={dark} className={className}>
      <Eyebrow className={`text-[11px] ${dark ? "text-[#7fd3e2]" : "text-[#6b7a8f]"}`}>{label}</Eyebrow>
      <div
        className={`mt-2.5 text-[34px] font-bold leading-none tabular-nums ${
          dark ? "text-white" : "text-[#071a33]"
        } ${valueClassName}`}
      >
        {value}
      </div>
      {subtext && <div className={`mt-1.5 text-[13px] ${dark ? "text-[#9fb6cf]" : "text-[#4a5361]"}`}>{subtext}</div>}
    </ModernCard>
  );
}

/** Login form field — label above input, mockup's exact spacing (not
 * ui.tsx's Field, which is sized for the classic theme's larger 18px-base
 * scale). */
export function ModernField({
  label,
  htmlFor,
  required = false,
  children,
  className = "",
}: {
  label: string;
  htmlFor: string;
  /** Marks the label with a red asterisk — purely visual, doesn't touch
   * validation (the underlying input's own `required` attribute, if any,
   * still does that work). Site-wide convention: every field the backend
   * actually rejects when empty gets this. */
  required?: boolean;
  children: ReactNode;
  className?: string;
} & Pick<LabelHTMLAttributes<HTMLLabelElement>, "className">) {
  return (
    <div className={`mb-3.5 ${className}`}>
      <label htmlFor={htmlFor} className="mb-1.5 block text-[12.5px] font-semibold text-[#4a5361]">
        {label}
        {required && (
          <span className="text-[#b3261e]" aria-hidden="true">
            {" "}
            *
          </span>
        )}
      </label>
      {children}
    </div>
  );
}

/** Opt-in forced-uppercase-while-typing, mirroring `ui.tsx`'s classic
 * `Input` ("matching this shop's old software") — but OFF by default here,
 * since ModernInput is used site-wide (Login, Settings, Admin, search
 * boxes...) and only Customer/Work Order/Technician forms actually want
 * this (see docs/FRONTEND_BACKLOG.md). Pass `uppercase` explicitly on
 * those fields only; never on email/password.
 *
 * `decimal` (also opt-in) restricts a price/quantity field to digits and a
 * single "." as the user types or pastes. */
export function ModernInput({
  onChange,
  uppercase,
  decimal,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { uppercase?: boolean; decimal?: boolean }) {
  return (
    <input
      {...(decimal ? { inputMode: "decimal" as const } : {})}
      {...props}
      className={`h-[46px] w-full rounded-[10px] border border-[#e2e7ee] px-3 text-[14px] text-[#101b2d] outline-none focus:border-[#0f6d86] disabled:bg-[#f6f8fb] disabled:text-[#6b7a8f] ${props.className ?? ""}`}
      onChange={(e) => {
        if (uppercase) applySameLengthTransform(e.target, (v) => v.toUpperCase());
        if (decimal) applyDecimalTransform(e.target);
        onChange?.(e);
      }}
    />
  );
}

/** `ModernInput` plus a Show/Hide toggle — same text-button-beside-the-
 * field pattern LoginPageModern.tsx's own sign-in field already uses
 * (kept as inline JSX there, not migrated to this shared version, since
 * it already works and isn't broken), just factored out so every other
 * password field in the app (Change Password, etc.) gets it too instead
 * of staying a plain unmaskable-only `type="password"` input. Manages
 * its own show/hide state internally — each instance is independent. */
export function ModernPasswordInput({
  className = "",
  ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type">) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <ModernInput {...props} type={show ? "text" : "password"} className={`flex-1 ${className}`} />
      <ModernButton
        type="button"
        variant="secondary"
        className="h-[46px] flex-none px-3.5"
        onClick={() => setShow((s) => !s)}
      >
        {show ? "Hide" : "Show"}
      </ModernButton>
    </div>
  );
}

export interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
}

/** A simple, dependency-free length+character-variety heuristic — NOT a
 * real entropy estimate (no password-strength library is installed in
 * this app). Complementary to, not a restatement of, PASSWORD_REQUIREMENTS
 * (packages/contracts/src/auth.ts) below: those are pass/fail and
 * enforced server-side; this rewards going beyond them (e.g. a longer
 * password scores higher even once every requirement is already met),
 * same idea as a typical signup form's strength bar. */
export function estimatePasswordStrength(password: string): PasswordStrength {
  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  const capped = Math.min(score, 4) as PasswordStrength["score"];
  const labels = ["Very weak", "Weak", "Fair", "Good", "Strong"];
  return { score: capped, label: labels[capped]! };
}

const STRENGTH_COLORS = ["#b3261e", "#c96a1e", "#b8860b", "#5a8f3c", "#1e7d32"];

/** A 4-segment strength bar + label, driven by estimatePasswordStrength()
 * above — renders nothing for an empty password (nothing to score yet). */
export function ModernPasswordStrengthMeter({ password }: { password: string }) {
  if (!password) return null;
  const { score, label } = estimatePasswordStrength(password);
  const color = STRENGTH_COLORS[score];
  return (
    <div className="mt-1.5">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-1.5 flex-1 rounded-full" style={{ backgroundColor: i < score ? color : "#e2e7ee" }} />
        ))}
      </div>
      <span className="mt-1 block text-[11.5px] font-semibold" style={{ color }}>
        {label}
      </span>
    </div>
  );
}

/** Live pass/fail checklist for every rule PASSWORD_REQUIREMENTS
 * (packages/contracts/src/auth.ts) actually enforces server-side — the
 * single shared list so a rule can never drift between what's displayed
 * here and what zNewPassword checks. Used by both password-change forms
 * (Owner Profile's Change Password, and the forced first-login change on
 * LoginPageModern) instead of each hand-rolling its own copy. */
export function ModernPasswordRequirementsList({ password }: { password: string }) {
  return (
    <ul className="m-0 mt-1.5 list-none p-0">
      {PASSWORD_REQUIREMENTS.map((req) => {
        const met = req.test(password);
        return (
          <li key={req.id} className={`flex items-center gap-1.5 text-[12px] ${met ? "text-[#1e7d32]" : "text-[#8b97a8]"}`}>
            <span aria-hidden="true">{met ? "✓" : "○"}</span>
            {req.label}
          </li>
        );
      })}
    </ul>
  );
}

/** Same visual language as ModernInput — added for docs/FRONTEND_BACKLOG.md
 * Round 60 (Work orders/Invoices/Customers/Technicians/Payments lists),
 * the first screens with a filter `<select>` in the mockup's own style
 * (see `Bay2Bill Redesign.dc.html`'s filter rows). `h-11` (44px) matches
 * ModernInput/ModernButton's own tap-target height rather than
 * ModernInput's 46px — the mockup's own filter selects/search boxes are
 * 44px, only the Login/form inputs are 46px. */
export function ModernSelect({ className = "", ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      className={`h-11 w-full rounded-[10px] border border-[#e2e7ee] bg-white px-3 text-[14px] text-[#101b2d] outline-none focus:border-[#0f6d86] disabled:bg-[#f6f8fb] disabled:text-[#6b7a8f] ${className}`}
    />
  );
}

/** Duplicated (not imported) from AppShellModern.tsx's own SearchIcon —
 * that file isn't a shared module, and this is the one other spot the
 * mockup uses the same glyph (search inputs on the Work orders/Customers
 * list filter rows, see lines ~199 and ~544 of the mockup). */
function SearchGlyph() {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 24 24"
      fill="none"
      stroke="#6b7a8f"
      strokeWidth={1.8}
      strokeLinecap="round"
      aria-hidden
      className="flex-none"
    >
      <circle cx={11} cy={11} r={7} />
      <path d="m16.5 16.5 4 4" />
    </svg>
  );
}

/** A `<label>` wrapping a search icon + bare `<input type="search">` —
 * the mockup's recurring filter-row search box (Work orders, Customers).
 * Takes the same props as a plain input; `aria-label` is required on the
 * caller's side since there's no visible `<label>` text here. */
export function ModernSearchInput({
  className = "",
  inputClassName = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { inputClassName?: string }) {
  return (
    <label
      className={`flex h-11 items-center gap-2 rounded-[10px] border border-[#e2e7ee] bg-white px-3 ${className}`}
    >
      <SearchGlyph />
      <input
        type="search"
        {...props}
        className={`w-full truncate border-0 bg-transparent text-[14px] text-[#101b2d] outline-none placeholder:text-[#8b97a8] ${inputClassName}`}
      />
    </label>
  );
}

/** Domain status vocabulary (work-order stage, invoice/billing status) ->
 * mockup color tokens — mirrors `ui.tsx`'s own (private) `STATUS_STYLES`/
 * `STATUS_LABELS` maps, deliberately duplicated rather than imported (see
 * this file's header comment on why this library stays separate from
 * ui.tsx's classic tokens). Colors come from the mockup's own `TONE` map
 * (see docs/FRONTEND_BACKLOG.md Round 60) — "invoiced"/"complete"/"paid"
 * share the same green, "in_progress"/"open"/"not_approved" share the same
 * amber, "approved"/"partially_paid" share the same teal. "void" has no
 * mockup swatch of its own (the mockup only shows it as a button's danger
 * color, `#b3261e`) so it's given a matching light-red chip here. */
const MODERN_STATUS_TONE: Record<string, { bg: string; fg: string }> = {
  estimate: { bg: "#eef1f6", fg: "#4a5361" },
  approved: { bg: "#e6f1f5", fg: "#0b5468" },
  in_progress: { bg: "#fdf3e0", fg: "#8a5c00" },
  complete: { bg: "#e7f4ea", fg: "#1a6b2c" },
  invoiced: { bg: "#e7f4ea", fg: "#1a6b2c" },
  void: { bg: "#fdecea", fg: "#b3261e" },
  open: { bg: "#fdf3e0", fg: "#8a5c00" },
  partially_paid: { bg: "#e6f1f5", fg: "#0b5468" },
  paid: { bg: "#e7f4ea", fg: "#1a6b2c" },
  not_approved: { bg: "#fdf3e0", fg: "#8a5c00" },
  not_invoiced: { bg: "#f1f4f9", fg: "#6b7a8f" },
  // Support inquiries — same amber/green pair as open/paid above, reused
  // for the same "needs attention" vs. "handled" meaning.
  new: { bg: "#fdf3e0", fg: "#8a5c00" },
  replied: { bg: "#e7f4ea", fg: "#1a6b2c" },
};

/** Same label text as ui.tsx's STATUS_LABELS — kept in sync by hand since
 * the two files don't share this map (see ui.tsx's own StatusBadge). */
const MODERN_STATUS_LABELS: Record<string, string> = {
  estimate: "Estimate",
  approved: "Approved",
  in_progress: "In Progress",
  complete: "Completed",
  invoiced: "Invoiced",
  void: "Void",
  open: "Not Paid",
  partially_paid: "Partially Paid",
  paid: "Paid in Full",
  not_approved: "Not Approved",
  not_invoiced: "Not Invoiced",
  unpaid: "Unpaid",
  new: "New",
  replied: "Replied",
};

/** The mockup's small rectangular (6px-radius) status/billing chip — NOT
 * the general 999px-pill shape used for buttons/filter-pills elsewhere in
 * the design; this exact shape+size is what every list-table status cell
 * in the mockup actually uses (see e.g. lines ~223-224, ~419 of
 * `Bay2Bill Redesign.dc.html`). Falls back to a neutral tone plus the raw
 * status string for anything unrecognized, same defensive default as
 * ui.tsx's StatusBadge. */
export function ModernStatusPill({ status, className = "" }: { status: string; className?: string }) {
  const tone = MODERN_STATUS_TONE[status] ?? { bg: "#eef1f6", fg: "#4a5361" };
  return (
    <span
      className={`inline-block whitespace-nowrap rounded-[6px] px-[9px] py-1 text-[11.5px] font-bold ${className}`}
      style={{ background: tone.bg, color: tone.fg }}
    >
      {MODERN_STATUS_LABELS[status] ?? status}
    </span>
  );
}

/** A borderless-card wrapper sized for a data table — same look as
 * ModernCard (white, 1px `#e2e7ee` border, 14px radius) but with no
 * padding of its own and `overflow-hidden` so the table's own header row
 * background reaches the card's rounded corners; the inner
 * `overflow-x-auto` div is what actually scrolls on a narrow screen (the
 * outer card never does, keeping the page itself from scrolling
 * sideways). Pair with ModernTh/ModernTd for the table's cells and
 * ModernPagination for its footer. */
export function ModernTableCard({ children, className = "" }: { children: ReactNode; className?: string }) {
  return (
    <div className={`overflow-hidden rounded-[14px] border border-[#e2e7ee] bg-white ${className}`}>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}

/** Roboto Mono, uppercase, 11px header cell — the mockup's one recurring
 * table-header style (`#f8fafc` row background, `#6b7a8f` text, 1px
 * `#e2e7ee` bottom border). Pass `onClick` for a sortable column (adds the
 * pointer cursor); the caller is responsible for rendering its own sort
 * arrow inside `children`, same as PaymentsReceivedReportPage.tsx already
 * does for the classic table. */
export function ModernTh({
  children,
  align = "left",
  className = "",
  ...props
}: ThHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right" }) {
  return (
    <th
      {...props}
      className={`whitespace-nowrap px-4 py-[11px] font-mono text-[11px] font-semibold uppercase tracking-[.1em] text-[#6b7a8f] ${
        align === "right" ? "text-right" : "text-left"
      } ${props.onClick ? "cursor-pointer select-none" : ""} ${className}`}
      style={{ fontFamily: "'Roboto Mono', ui-monospace, monospace", ...props.style }}
    >
      {children}
    </th>
  );
}

/** Body cell matching ModernTh's table — 14px/16px padding and a faint
 * `#f1f4f9` row divider (lighter than the header's own border, matching
 * the mockup exactly). `top` (the default) vertical-aligns a multi-line
 * cell's first line with a single-line neighbor; pass `align="middle"` for
 * a short/single-value cell where that matters less. */
export function ModernTd({
  children,
  align = "left",
  valign = "top",
  className = "",
  ...props
}: TdHTMLAttributes<HTMLTableCellElement> & { align?: "left" | "right"; valign?: "top" | "middle" }) {
  return (
    <td
      {...props}
      className={`px-4 py-3.5 text-[14px] text-[#101b2d] ${align === "right" ? "text-right" : "text-left"} ${
        valign === "top" ? "align-top" : "align-middle"
      } ${className}`}
      style={{ borderTop: "1px solid #f1f4f9", ...props.style }}
    >
      {children}
    </td>
  );
}

/** Shared pagination footer for the modern theme's list pages — same
 * page/pageSize/total contract as ui.tsx's own `Pagination` (this app's
 * classic list pages all share that one component; this is its modern
 * counterpart), restyled to the mockup's compact footer look. Pass
 * `bordered={false}` for a card-grid page (Customers/Technicians), which
 * has no enclosing ModernTableCard for this to sit inside of. */
export function ModernPagination({
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  bordered = true,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  bordered?: boolean;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-[13px] text-[#6b7a8f] ${
        bordered ? "border-t border-[#e2e7ee]" : "mt-4 px-0"
      }`}
    >
      <span>{total === 0 ? "No results" : `Showing ${start}–${end} of ${total}`}</span>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5">
          Rows
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label="Rows per page"
            className="h-8 rounded-lg border border-[#e2e7ee] bg-white px-2 text-[12.5px] text-[#101b2d] outline-none focus:border-[#0f6d86]"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className="h-9 rounded-lg border border-[#e2e7ee] bg-white px-3 text-[13px] font-medium text-[#101b2d] disabled:cursor-not-allowed disabled:text-[#8b97a8]"
        >
          ← Previous
        </button>
        <span>
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          className="h-9 rounded-lg border border-[#e2e7ee] bg-white px-3 text-[13px] font-medium text-[#101b2d] disabled:cursor-not-allowed disabled:text-[#8b97a8]"
        >
          Next →
        </button>
      </div>
    </div>
  );
}

/** Circular initials badge — the mockup's recurring "AK"-style avatar
 * (Customer detail's header, Customers list card grid, Technicians card
 * grid). `size`/`fontSize` default to the Customer-detail header's own
 * 52px/17px; pass smaller values for a denser context (e.g. a list row). */
export function AvatarBadge({
  initials,
  size = 52,
  fontSize = 17,
  className = "",
}: {
  initials: string;
  size?: number;
  fontSize?: number;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex flex-none items-center justify-center rounded-full bg-[#e6f1f5] font-bold text-[#0f6d86] ${className}`}
      style={{ height: size, width: size, fontSize }}
    >
      {initials}
    </span>
  );
}

/** Modern counterpart of ui.tsx's `ConfirmButton` — a click opens a
 * confirmation popup (not an inline expand, which a stray double-click could
 * confirm) and nothing fires until the popup's confirm button is clicked. A
 * "danger" variant gets a red confirm button. Every destructive action
 * already gated by `ConfirmButton` in the classic theme (delete customer,
 * delete vehicle, etc.) must keep asking before it fires here too — this is
 * that gate, not a new one. */
export function ModernConfirmButton({
  onConfirm,
  confirmText = "Are you sure?",
  confirmLabel = "Yes, Do It",
  children,
  variant = "danger",
  className = "",
  ...props
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick"> & {
  onConfirm: () => void;
  confirmText?: string;
  confirmLabel?: string;
  variant?: keyof typeof MODERN_BUTTON_VARIANTS;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <ModernButton type="button" variant={variant} className={className} onClick={() => setConfirming(true)} {...props}>
        {children}
      </ModernButton>
      <ModernConfirmDialog
        open={confirming}
        message={confirmText}
        confirmLabel={confirmLabel}
        danger={variant === "danger"}
        onConfirm={() => {
          setConfirming(false);
          onConfirm();
        }}
        onCancel={() => setConfirming(false)}
      />
    </>
  );
}

/** Modern counterpart of ui.tsx's `Breadcrumbs` — same one-current-page,
 * rest-are-links contract, restyled to the mockup's own breadcrumb (12.5px,
 * `#6b7a8f` trail, `#101b2d` current page, `/` separators with 7px
 * padding — see e.g. `Bay2Bill Redesign.dc.html` line ~579). */
export function ModernBreadcrumbs({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-3 flex flex-wrap items-center text-[12.5px] text-[#6b7a8f]">
      {items.map((item, i) => (
        <span key={i} className="flex items-center">
          {i > 0 && (
            <span aria-hidden className="px-[7px]">
              /
            </span>
          )}
          {item.to ? (
            <Link to={item.to} className="text-[#6b7a8f] no-underline hover:text-[#0f6d86]">
              {item.label}
            </Link>
          ) : (
            <span className="text-[#101b2d]">{item.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/** Same visual language as ModernInput, multi-line — added for
 * docs/FRONTEND_BACKLOG.md Round 59's Shop Settings screen ("Invoice
 * wording" card's terms textarea, see `Bay2Bill Redesign.dc.html` line
 * ~761). Unlike ModernInput's fixed 46px height, height is left to the
 * caller (`rows` prop) since a textarea's natural sizing is by row count. */
/** Same opt-in `uppercase` support as ModernInput above — see that
 * component's own comment for why it's off by default. */
export function ModernTextarea({ onChange, uppercase, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { uppercase?: boolean }) {
  return (
    <textarea
      {...props}
      className={`w-full resize-y rounded-[10px] border border-[#e2e7ee] px-3 py-2.5 text-[14px] text-[#101b2d] outline-none focus:border-[#0f6d86] disabled:bg-[#f6f8fb] disabled:text-[#6b7a8f] ${props.className ?? ""}`}
      onChange={(e) => {
        if (uppercase) applySameLengthTransform(e.target, (v) => v.toUpperCase());
        onChange?.(e);
      }}
    />
  );
}

/** The Work Order Editor's horizontal stage tracker — see
 * docs/FRONTEND_BACKLOG.md Round 60 and `Bay2Bill Redesign.dc.html` lines
 * ~276-282 (the `stages`/`st.bg`/`st.fg`/`st.mark` loop). The mockup's own
 * `STAGES` array is static demo data (hardcoded per-stage colors); this
 * derives the same 3 visual states (past/current/future) from a REAL
 * `status`, mirroring `WorkOrderEditorPageClassic.tsx`'s own `StageTracker`
 * (`STAGE_ORDER`/`STAGE_LABELS`, "invoiced" counts as having reached the
 * last stage, "void" is expected to be hidden by the caller rather than
 * rendered here — same as Classic's own `status !== "void" &&` gate). Past
 * stages get a green check, the current stage a solid navy fill, future
 * stages a muted outline-less look — exactly the 3 tones the mockup's own
 * `STAGES` array demonstrates. */
export function ModernStageTracker({
  order,
  labels,
  status,
}: {
  order: readonly string[];
  labels: Record<string, string>;
  status: string;
}) {
  const currentIndex = order.indexOf(status);
  const effectiveIndex = status === "invoiced" ? order.length - 1 : currentIndex;
  if (effectiveIndex < 0) return null;
  return (
    <div
      className="mb-5 flex items-center gap-0 overflow-x-auto rounded-xl border border-[#e2e7ee] bg-white p-1.5"
      aria-label="Work order stage"
    >
      {order.map((stage, i) => {
        const tone =
          i < effectiveIndex
            ? { bg: "#e7f4ea", fg: "#1a6b2c", mark: "✓" }
            : i === effectiveIndex
              ? { bg: "#071a33", fg: "#ffffff", mark: String(i + 1) }
              : { bg: "transparent", fg: "#8b97a8", mark: String(i + 1) };
        return (
          <span
            key={stage}
            className="flex min-w-[120px] flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg px-3 py-2.5 text-[13px] font-semibold"
            style={{ background: tone.bg, color: tone.fg }}
          >
            <span
              className="inline-flex h-[18px] w-[18px] items-center justify-center rounded-full border-[1.5px] text-[10px] font-bold"
              style={{ borderColor: "currentColor" }}
            >
              {tone.mark}
            </span>
            {labels[stage]}
          </span>
        );
      })}
    </div>
  );
}

/** A checkbox with its label text wrapping alongside it — the mockup's
 * recurring "Where the logo shows" style toggle row (see
 * `Bay2Bill Redesign.dc.html` lines ~795-799: 17px checkbox, `flex-start`
 * aligned so a second line of label text lines up under the first rather
 * than centering against the whole label block). `hint` renders a small
 * italic note under the label — used for Shop Settings' logo/name-in-header
 * interdependency explanation ("can't turn off with the shop name also
 * hidden from the header"), matching what
 * `SettingsPageClassic.tsx`/`ui.tsx`'s plain checkboxes show inline today. */
export function ModernCheckboxRow({
  checked,
  onChange,
  disabled = false,
  children,
  hint,
  className = "",
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  children: ReactNode;
  hint?: ReactNode;
  className?: string;
}) {
  return (
    <label
      className={`flex items-start gap-2.5 text-[13.5px] leading-snug text-[#101b2d] ${
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      } ${className}`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-[2px] h-[17px] w-[17px] flex-none rounded border border-[#e2e7ee]"
      />
      <span>
        {children}
        {hint && <span className="block text-[12px] italic text-[#8b97a8]">{hint}</span>}
      </span>
    </label>
  );
}

/** Modern counterpart of `ui.tsx`'s `Modal` — identical props/contract (a
 * drop-in replacement), restyled to the mockup's own dialog look: `16px`
 * radius, no border, the mockup's own elevated-dialog shadow
 * (`0 20px 50px rgba(0,0,0,.35)`, the same one Login's own sign-in card
 * uses). Every Modern screen with a confirm/choice dialog was still
 * rendering the CLASSIC `Modal`/`ConfirmDialog` unstyled inside an
 * otherwise fully modern page — this is the fix. */
/** Deliberately does NOT close on a backdrop/outside click — a modal here
 * is usually mid-form-entry (Add Part/Labor/Sublet, etc.), and a stray
 * click just outside its bounds used to silently discard whatever the
 * user had already typed. Only the explicit Cancel button (rendered by
 * each caller's own `children`) or this component's own X icon below
 * ever calls `onCancel`. */
export function ModernModal({
  title,
  children,
  onCancel,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onCancel: () => void;
  /** Wider dialog, for content like an email preview. */
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className={`w-full ${wide ? "max-w-2xl" : "max-w-md"} rounded-2xl bg-white p-6`} style={{ boxShadow: "0 20px 50px rgba(0,0,0,.35)" }}>
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 className="text-[19px] font-bold text-[#071a33]">{title}</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onCancel}
            className="-mr-1 -mt-1 flex h-9 w-9 flex-none items-center justify-center rounded-full text-2xl leading-none text-[#8b97a8] hover:bg-[#f1f4f9] hover:text-[#071a33]"
          >
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Modern counterpart of `ui.tsx`'s `ConfirmDialog` — identical
 * props/contract (pair with the SAME `useConfirmGate()` hook Classic
 * uses), built on `ModernModal` the same way Classic's own version is
 * built on its own `Modal`. */
export function ModernConfirmDialog({
  open,
  message,
  confirmLabel = "Yes, Save It",
  danger = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  message: string;
  confirmLabel?: string;
  /** Red confirm button — for void/delete. */
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // A double-click on the button that opened this dialog can land its second
  // click on the confirm button (the dialog appears under the cursor), so
  // confirm clicks in the first moments after opening are ignored.
  const openedAt = useRef(0);
  useEffect(() => {
    if (open) openedAt.current = Date.now();
  }, [open]);
  if (!open) return null;
  return (
    <ModernModal title="Please Confirm" onCancel={onCancel}>
      <p className="mb-6 text-[14px] text-[#101b2d]">{message}</p>
      <div className="flex gap-2.5">
        <ModernButton
          type="button"
          variant={danger ? "danger" : "primary"}
          onClick={() => {
            if (Date.now() - openedAt.current < 600) return;
            onConfirm();
          }}
        >
          {confirmLabel}
        </ModernButton>
        <ModernButton type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </ModernButton>
      </div>
    </ModernModal>
  );
}

/** Modern counterparts of `ui.tsx`'s `ErrorBanner`/`SuccessBanner`/
 * `NoticeBanner` — identical contracts, restyled to the mockup's own
 * subtler, smaller-type banner language instead of Classic's bold
 * 2px-border/18px-text one. Danger/success colors are this app's existing
 * `#b3261e`/`#1a6b2c` (already used elsewhere in this file), just on a
 * lighter tint background instead of Classic's `-soft` tokens. */
export function ModernErrorBanner({ message }: { message: string }) {
  return (
    <div role="alert" className="mb-4 rounded-[10px] border border-[#f3d7d5] bg-[#fdecea] px-4 py-3 text-[13.5px] font-medium text-[#b3261e]">
      {message}
    </div>
  );
}

export function ModernSuccessBanner({ message }: { message: string }) {
  return (
    <div role="status" className="mb-4 rounded-[10px] border border-[#cfe8d4] bg-[#e7f4ea] px-4 py-3 text-[13.5px] font-medium text-[#1a6b2c]">
      {message}
    </div>
  );
}

export function ModernNoticeBanner({ notice }: { notice: { text: string; isError: boolean } | null }) {
  if (!notice) return null;
  return notice.isError ? <ModernErrorBanner message={notice.text} /> : <ModernSuccessBanner message={notice.text} />;
}

/** Modern counterpart of `ui.tsx`'s `EmptyState` — identical contract. */
export function ModernEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#e2e7ee] p-10 text-center text-[14px] text-[#8b97a8]">{children}</div>
  );
}

/** Modern counterpart of `ui.tsx`'s `InfoTip` — identical contract, but
 * deliberately quieter: the classic version's `bg-ink` (near-black) tooltip
 * plus a bold 2px-border "?" badge read as heavy/loud once dropped into
 * the modern theme's otherwise much lighter-weight, thin-border visual
 * language (owner's own word for it, 2026-09-13) — this drops to a 1px
 * border, smaller 18px badge, and the same
 * pale-teal surface and font (TINT_SURFACE/TINT_TEXT) as ModernTintButton for the tooltip. It resets
 * text-transform/tracking so it stays mixed case inside uppercase headings. */
export function ModernInfoTip({
  text,
  align = "center",
  below = false,
}: {
  text: string;
  align?: "center" | "end" | "start";
  /** Open the tooltip under the icon (for tips near the top edge of the page). */
  below?: boolean;
}) {
  return (
    <span className="group relative inline-flex">
      <span
        tabIndex={0}
        aria-label={text}
        className="inline-flex h-[18px] w-[18px] cursor-help items-center justify-center rounded-full border border-[#c3ccd9] text-[11px] font-semibold text-[#6b7a8f]"
      >
        ?
      </span>
      <span
        className={`pointer-events-none absolute z-10 w-60 ${below ? "top-full mt-2" : "bottom-full mb-2"} rounded-lg ${TINT_SURFACE} px-3 py-2 font-sans ${TINT_TEXT} normal-case leading-snug tracking-normal opacity-0 shadow-[0_10px_30px_rgba(0,0,0,.15)] transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 ${
          align === "end" ? "right-0" : align === "start" ? "left-0" : "left-1/2 -translate-x-1/2"
        }`}
      >
        {text}
      </span>
    </span>
  );
}

/** Modern counterpart of `ui.tsx`'s `Footer` — same legal copy (the
 * owner's own exact wording, not paraphrased), restyled to the modern
 * theme's lighter, smaller-type register instead of Classic's bold
 * 2px-border one. */
// Legal pages live on the marketing site (built from bay2bill-web-ops/legal/).
// Same VITE_MARKETING_URL override as LoginPageModern's "Back to" link.
const LEGAL_BASE_URL = (import.meta.env.VITE_MARKETING_URL || "https://bay2bill.com").replace(/\/+$/, "");

export function ModernFooter() {
  return (
    <footer className="border-t border-[#e2e7ee] px-6 py-4 text-center text-[12.5px] text-[#8b97a8]">
      <p>Bay2Bill is operated by Scanvato Labs LLC.</p>
      <p>© 2026 Scanvato Labs LLC. All rights reserved.</p>
      <p className="mt-1 flex justify-center gap-4">
        <a href={`${LEGAL_BASE_URL}/terms.html`} target="_blank" rel="noreferrer" className="hover:text-[#0f6d86] hover:underline">
          Terms of Service
        </a>
        <a href={`${LEGAL_BASE_URL}/privacy.html`} target="_blank" rel="noreferrer" className="hover:text-[#0f6d86] hover:underline">
          Privacy Policy
        </a>
      </p>
    </footer>
  );
}
